import { useState, useEffect, useMemo, useCallback } from "react";
import classNames from "classnames";
import update from "immutability-helper";
import { Form, Formik } from "formik";
import AscAddActivity from "./AscAddActivity/AscAddActivity";
import AscButton from "../../AscButton/AscButton";
import AscTabs from "../../AscTabs/AscTabs";
import AscHeading from "../../AscTypography/AscHeading/AscHeading";
import AscAddModuleFooter from "../AscAddModuleFooter/AscAddModuleFooter";
import {
  ACTIVITY_TYPE_OPTIONS,
  ActivityAttemptsSelectOptions,
  ActivityTypeSelectOptions,
  AdminActivitiesForm,
  AdminActivity,
} from "@ascendnow/shared";
import styles from "./AscAddModuleActivities.module.scss";
import { useAscAddModuleActivitiesForm } from "./useAscAddModuleActivitiesForm";
import AscErrorBoundary from "../../AscErrorBoundary/AscErrorBoundary";

export interface AscAddModuleActivitiesProps {
  className?: string;
  header: string;
  value: number;
  currentActivities?: AdminActivity[];
  setValue: (value: number) => void;
  addActivity?: (activity: AdminActivity, index: number) => void;
  activities: ActivityTypeSelectOptions["value"][];
  setActivities: (value: ActivityTypeSelectOptions["value"][]) => void;
  activityAttempt: ActivityAttemptsSelectOptions["value"][];
  setActivityAttempt: (value: ActivityAttemptsSelectOptions["value"][]) => void;
}

export function AscAddModuleActivities({
  className,
  header,
  value,
  currentActivities,
  setValue,
  activities,
  setActivities,
  activityAttempt,
  setActivityAttempt,
}: AscAddModuleActivitiesProps) {
  const [currentTab, setCurrentTab] = useState<string>("0");
  const [loading, setLoading] = useState(true);
  const formik = useAscAddModuleActivitiesForm();
  //Since an activity can only be added once, we need to filter out
  //the ones that are already added
  const activitiesNotAlreadyAdded = useMemo(() => {
    return ACTIVITY_TYPE_OPTIONS?.filter(
      (a) => !activities?.some((addedActivity) => addedActivity === a.value)
    );
  }, [activities]);

  const handleChangeType = (
    value: ActivityTypeSelectOptions["value"] | undefined,
    index: number
  ) => {
    if (value === undefined) return;
    const newArr = [...activities];
    newArr[index] = value;
    setActivities(newArr);
  };

  const handleChangeAttempt = (
    value: ActivityAttemptsSelectOptions["value"] | undefined,
    index: number
  ) => {
    if (value === undefined) return;
    const newArr = [...activityAttempt];
    newArr[index] = value;
    setActivityAttempt(newArr);
  };

  const submitAndNext = () => {
    formik.handleSubmit();
    setValue(value + 1);
  };

  const submitAndBack = () => {
    formik.handleSubmit();
    setValue(value - 1);
  };

  useEffect(() => {
    if (currentActivities && currentActivities.length > 0) {
      const types: ActivityTypeSelectOptions["value"][] = [];
      const attempts: ActivityAttemptsSelectOptions["value"][] = [];
      currentActivities.forEach((currentAct) => {
        types.push(currentAct?.type);
        attempts.push(
          currentAct?.attemptsNum?.toString() as ActivityAttemptsSelectOptions["value"]
        );
        formik.setFieldValue(currentAct?.type, currentAct);
      });
      setActivities(types);
      setActivityAttempt(attempts);
    }
    setLoading(false);
  }, []);

  //Order button handlers
  const handleOrderBack = () => {
    const index = Number(currentTab);
    if (index === 0) {
      return;
    }
    const data = [...activities];
    const temp = activities[index - 1];

    data[index - 1] = activities[index];
    data[index] = temp;

    setActivities(data);
    setCurrentTab(`${index - 1}`);
  };

  const handlerOrderForward = () => {
    const index = Number(currentTab);
    if (index + 1 === activities.length) {
      return;
    }

    const data = [...activities];
    const temp = activities[index + 1];
    data[index + 1] = activities[index];
    data[index] = temp;

    setActivities(data);
    setCurrentTab(`${index + 1}`);
  };

  const handleCustomSelect = (id:string)=>{
    setCurrentTab(id)
  }
  return (
    <div className={classNames(styles.ground, className)}>
      <div className={styles.order_btns}>
        <AscButton onClick={handleOrderBack}>{"<-"}</AscButton>
        <AscButton onClick={handlerOrderForward}>{"->"}</AscButton>
      </div>
      <div className={styles.topGround}>
        <AscHeading className={styles.header} level={6}>
          {header}
        </AscHeading>

        <AscButton
          className={styles.link}
          variant="link"
          hideLinkIcon={true}
          onClick={() => {
            const activityToAdd = activitiesNotAlreadyAdded?.[0]?.value;
            if (!activityToAdd) return;
            setActivities([...activities, activityToAdd]);
            setActivityAttempt([...activityAttempt, "3"]);
          }}
        >
          Add activity <span>+</span>
        </AscButton>
      </div>
      {!loading && activities && (
        <AscTabs
          type="secondary"
          selectedId={currentTab}
          className={styles.tabsGround}
          tabContentClassName={styles.tabContentClassName}
          navigationClassName={styles.navigationClassName}
          onChange={handleCustomSelect}
        >
          {activities?.map((activity, index) => (
            <AscTabs.Tab key={index} id={index.toString()} title={`Activity ${index + 1}`}>
              <AscErrorBoundary>
                <AscAddActivity
                  order={index}
                  activityType={activity}
                  activityAttempt={activityAttempt[index]}
                  onAttemptChange={(val) => handleChangeAttempt(val, index)}
                  onTypeChange={(val) => handleChangeType(val, index)}
                  selectableActivityTypes={[
                    //add the current activity to the list of selectable activities
                    ...ACTIVITY_TYPE_OPTIONS.filter(
                      (a) => a.value === activity
                    ),
                    ...activitiesNotAlreadyAdded,
                  ]}
                />
              </AscErrorBoundary>
            </AscTabs.Tab>
          ))}
        </AscTabs>
      )}
      <AscAddModuleFooter
        className={styles.footer}
        currentValue={value}
        goNext={submitAndNext}
        goBack={submitAndBack}
      />
    </div>
  );
}

type AscAddModuleActivitiesPropsWithFormikWrapperProps = Omit<
  AscAddModuleActivitiesProps,
  "activities" | "setActivities" | "activityAttempt" | "setActivityAttempt"
>;

export default function AscAddModuleActivitiesWithFormikWrapper({
  addActivity,
  ...rest
}: AscAddModuleActivitiesPropsWithFormikWrapperProps) {
  const [activities, setActivities] = useState<
    ActivityTypeSelectOptions["value"][]
  >(["assessment"]);
  const [activityAttempt, setActivityAttempt] = useState<
    ActivityAttemptsSelectOptions["value"][]
  >(["3"]);
  return (
    <Formik<AdminActivitiesForm>
      initialValues={{
        dragDrop: {
          questions: { cards: [], groups: [], correctAnswers: [] },
        },
        crossword: {
          questions: { acrossQuestions: [], downQuestions: [], size: 10 },
        },
        findWord: {
          questions: {
            questions: [],
          },
        },
        timeline: {
          questions: { cards: [], groups: [], correctAnswers: [] },
        },
        trueFalse: {
          questions: { questions: [] },
        },
        markWord: {
          questions: {
            question: "",
            text: "",
            answers: [],
          },
        },
        dragWord: {
          questions: { questions: [] },
        },
        essay: {
          questions: { name: "", description: "" },
        },
        flashcards: {
          questions: { questions: [] },
          image: [],
        },
        quiz: {
          questions: { questions: [] },
        },
        quizDragDrop: {
          questions: {
            questions: {
              answers: [],
            },
          },
          image: [],
        },
        assessment: {
          questions: { questions: [] },
        },
      }}
      onSubmit={(values) => {
        activities.forEach((activityType, index: number) => {
          const activity = values[activityType];
          if (!activity) return;
          addActivity?.(
            {
              id: activity?.id,
              type: activityType,
              attemptsNum: Number(activityAttempt[index]),
              questions: activity?.questions,
              image: activity?.image,
            },
            index
          );
        });
      }}
    >
      <Form>
        <AscAddModuleActivities
          {...rest}
          activities={activities}
          setActivities={setActivities}
          activityAttempt={activityAttempt}
          setActivityAttempt={setActivityAttempt}
        />
      </Form>
    </Formik>
  );
}
